import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { 
  CheckCircle2, XCircle, Camera, User, Calendar, 
  CreditCard, Clock, Loader2, AlertTriangle, Check,
  Banknote, Receipt, Edit2, Save, X, FileText, Lock,
  Plus, Trash2, Scan
} from 'lucide-react';
import { format, isValid, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import PassportScanner from '../PassportScanner';
import { printPoliceForm } from '../../utils/policeFormTemplate';

// Helper to safely format dates (handles invalid dates from OCR)
const safeFormatDate = (dateValue, formatStr = 'dd/MM/yyyy') => {
  if (!dateValue) return '-';
  try {
    // Try parsing as ISO string first
    let date = typeof dateValue === 'string' ? parseISO(dateValue) : new Date(dateValue);
    if (!isValid(date)) {
      // Try direct Date parsing
      date = new Date(dateValue);
    }
    if (!isValid(date)) return '-';
    return format(date, formatStr);
  } catch {
    return '-';
  }
};

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Countries for flag display
const COUNTRY_FLAGS = {
  FR: '🇫🇷', MA: '🇲🇦', BE: '🇧🇪', CH: '🇨🇭', DE: '🇩🇪', GB: '🇬🇧',
  ES: '🇪🇸', IT: '🇮🇹', NL: '🇳🇱', US: '🇺🇸', CA: '🇨🇦', PT: '🇵🇹',
  DZ: '🇩🇿', TN: '🇹🇳', AE: '🇦🇪', SA: '🇸🇦', JP: '🇯🇵', CN: '🇨🇳',
  AU: '🇦🇺', BR: '🇧🇷'
};

const COUNTRIES = [
  { code: 'FR', name: 'France' },
  { code: 'MA', name: 'Maroc' },
  { code: 'BE', name: 'Belgique' },
  { code: 'CH', name: 'Suisse' },
  { code: 'DE', name: 'Allemagne' },
  { code: 'GB', name: 'Royaume-Uni' },
  { code: 'ES', name: 'Espagne' },
  { code: 'IT', name: 'Italie' },
  { code: 'NL', name: 'Pays-Bas' },
  { code: 'US', name: 'États-Unis' },
  { code: 'CA', name: 'Canada' },
  { code: 'PT', name: 'Portugal' },
  { code: 'DZ', name: 'Algérie' },
  { code: 'TN', name: 'Tunisie' },
  { code: 'AE', name: 'Émirats' },
  { code: 'SA', name: 'Arabie S.' },
  { code: 'JP', name: 'Japon' },
  { code: 'CN', name: 'Chine' },
  { code: 'AU', name: 'Australie' },
  { code: 'BR', name: 'Brésil' }
];

// Add PAX Form with Scanner
const AddPaxForm = ({ reservationId, onSave, onCancel }) => {
  const [mode, setMode] = useState('choice'); // 'choice', 'scanner', 'manual'
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    nationality: '',
    birth_date: '',
    document_number: '',
    document_expiry: ''
  });
  const [saving, setSaving] = useState(false);

  const handleScanComplete = (data) => {
    setForm({
      first_name: data.first_name || '',
      last_name: data.last_name || '',
      nationality: data.nationality || '',
      birth_date: data.birth_date || '',
      document_number: data.document_number || '',
      document_expiry: ''
    });
    setMode('manual'); // Switch to manual mode to complete/validate
    toast.success('Données du scan importées. Vérifiez et complétez si nécessaire.');
  };

  const handleSave = async () => {
    if (!form.first_name?.trim() || !form.last_name?.trim()) {
      toast.error('Prénom et nom requis');
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/reservations/${reservationId}/pax`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(form)
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Erreur lors de l\'ajout');
      }

      const newPax = await response.json();
      toast.success('Voyageur ajouté');
      onSave(newPax);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Choice mode
  if (mode === 'choice') {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl w-full max-w-md">
          <div className="p-4 border-b">
            <h3 className="font-semibold">Ajouter un voyageur</h3>
            <p className="text-sm text-gray-500">Choisissez comment saisir les informations</p>
          </div>
          <div className="p-4 space-y-3">
            <Button 
              onClick={() => setMode('scanner')} 
              className="w-full h-16 bg-blue-600 hover:bg-blue-700"
            >
              <Scan className="w-6 h-6 mr-3" />
              <div className="text-left">
                <p className="font-semibold">Scanner le passeport</p>
                <p className="text-xs opacity-80">Remplissage automatique par OCR</p>
              </div>
            </Button>
            <Button 
              onClick={() => setMode('manual')} 
              variant="outline"
              className="w-full h-16"
            >
              <Edit2 className="w-6 h-6 mr-3" />
              <div className="text-left">
                <p className="font-semibold">Saisie manuelle</p>
                <p className="text-xs text-gray-500">Entrer les informations à la main</p>
              </div>
            </Button>
          </div>
          <div className="p-4 border-t">
            <Button variant="ghost" onClick={onCancel} className="w-full">
              Annuler
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Scanner mode
  if (mode === 'scanner') {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
        <div className="bg-white rounded-xl w-full max-w-lg my-4">
          <div className="p-4 border-b flex items-center justify-between">
            <h3 className="font-semibold">Scanner le passeport</h3>
            <Button size="sm" variant="ghost" onClick={() => setMode('choice')}>
              <X className="w-4 h-4" />
            </Button>
          </div>
          <div className="p-4">
            <PassportScanner 
              onScanComplete={handleScanComplete}
              onCancel={() => setMode('choice')}
            />
          </div>
        </div>
      </div>
    );
  }

  // Manual mode (or post-scan validation)
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-md">
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <h3 className="font-semibold">Nouveau voyageur</h3>
            <p className="text-sm text-gray-500">Remplissez les informations</p>
          </div>
          <Button size="sm" variant="ghost" onClick={onCancel}>
            <X className="w-4 h-4" />
          </Button>
        </div>
        <div className="p-4 space-y-3 max-h-[60vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-600">Prénom *</label>
              <Input
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                className="h-9"
                placeholder="Jean"
              />
            </div>
            <div>
              <label className="text-xs text-gray-600">Nom *</label>
              <Input
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                className="h-9"
                placeholder="Dupont"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-600">Nationalité</label>
              <select
                value={form.nationality}
                onChange={(e) => setForm({ ...form, nationality: e.target.value })}
                className="w-full h-9 px-2 border rounded-md text-sm"
              >
                <option value="">Sélectionner...</option>
                {COUNTRIES.map(c => (
                  <option key={c.code} value={c.code}>{COUNTRY_FLAGS[c.code]} {c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-600">Date de naissance</label>
              <Input
                type="date"
                value={form.birth_date}
                onChange={(e) => setForm({ ...form, birth_date: e.target.value })}
                className="h-9"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-600">N° Document</label>
              <Input
                value={form.document_number}
                onChange={(e) => setForm({ ...form, document_number: e.target.value.toUpperCase() })}
                className="h-9 font-mono"
                placeholder="AB1234567"
              />
            </div>
            <div>
              <label className="text-xs text-gray-600">Expiration</label>
              <Input
                type="date"
                value={form.document_expiry}
                onChange={(e) => setForm({ ...form, document_expiry: e.target.value })}
                className="h-9"
              />
            </div>
          </div>
        </div>
        <div className="p-4 border-t flex gap-2">
          <Button variant="outline" onClick={onCancel} className="flex-1">
            Annuler
          </Button>
          <Button onClick={handleSave} disabled={saving} className="flex-1 bg-green-600 hover:bg-green-700">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
            Ajouter
          </Button>
        </div>
      </div>
    </div>
  );
};

// Editable Pax Card
const PaxCard = ({ pax, index, onUploadPhoto, uploading, onEdit, onSave, editing, reservationId, onDelete, paxLocked }) => {
  const fileInputRef = useRef(null);
  const [editForm, setEditForm] = useState({
    first_name: pax.first_name || '',
    last_name: pax.last_name || '',
    nationality: pax.nationality || '',
    birth_date: pax.birth_date || '',
    document_number: pax.document_number || '',
    document_expiry: pax.document_expiry || ''
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isReady = pax.is_ready;
  const hasPhoto = pax.has_photo;
  const isAdult = pax.is_adult;
  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      onUploadPhoto(pax.id, file);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/reservations/${reservationId}/pax/${pax.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(editForm)
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Erreur lors de la mise à jour');
      }

      toast.success('Voyageur mis à jour');
      onSave(pax.id, editForm);
    } catch (err) {
      toast.error(err.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Supprimer ${pax.first_name} ${pax.last_name} ?`)) return;
    
    setDeleting(true);
    try {
      const response = await fetch(`${API_URL}/api/reservations/${reservationId}/pax/${pax.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Erreur lors de la suppression');
      }

      toast.success('Voyageur supprimé');
      onDelete(pax.id);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDeleting(false);
    }
  };

  // Edit mode
  if (editing === pax.id) {
    return (
      <div className="p-4 rounded-xl border-2 border-blue-300 bg-blue-50">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-blue-900">Modification du voyageur</h4>
          <Button size="sm" variant="ghost" onClick={() => onEdit(null)}>
            <X className="w-4 h-4" />
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-600">Prénom *</label>
            <Input
              value={editForm.first_name}
              onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
              className="h-9"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600">Nom *</label>
            <Input
              value={editForm.last_name}
              onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
              className="h-9"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600">Nationalité *</label>
            <select
              value={editForm.nationality}
              onChange={(e) => setEditForm({ ...editForm, nationality: e.target.value })}
              className="w-full h-9 px-2 border rounded-md text-sm"
            >
              <option value="">Sélectionner...</option>
              {COUNTRIES.map(c => (
                <option key={c.code} value={c.code}>{COUNTRY_FLAGS[c.code]} {c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-600">Date de naissance *</label>
            <Input
              type="date"
              value={editForm.birth_date}
              onChange={(e) => setEditForm({ ...editForm, birth_date: e.target.value })}
              className="h-9"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600">N° Document *</label>
            <Input
              value={editForm.document_number}
              onChange={(e) => setEditForm({ ...editForm, document_number: e.target.value.toUpperCase() })}
              className="h-9 font-mono"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600">Expiration *</label>
            <Input
              type="date"
              value={editForm.document_expiry}
              onChange={(e) => setEditForm({ ...editForm, document_expiry: e.target.value })}
              className="h-9"
            />
          </div>
        </div>
        <div className="flex gap-2 mt-3">
          <Button size="sm" variant="outline" onClick={() => onEdit(null)} className="flex-1">
            Annuler
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving} className="flex-1 bg-blue-600 hover:bg-blue-700">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            Enregistrer
          </Button>
        </div>
      </div>
    );
  }

  // View mode
  return (
    <div className={`p-4 rounded-xl border-2 ${
      isReady 
        ? 'border-green-200 bg-green-50' 
        : pax.is_complete 
          ? 'border-blue-200 bg-blue-50'
          : 'border-amber-200 bg-amber-50'
    }`}>
      <div className="flex items-start gap-3">
        {/* Status icon */}
        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold shrink-0 ${
          isReady ? 'bg-green-500' : pax.is_complete ? 'bg-blue-500' : 'bg-amber-500'
        }`}>
          {isReady ? (
            <Check className="w-5 h-5" />
          ) : (
            <User className="w-5 h-5" />
          )}
        </div>
        
        {/* Pax info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <p className="font-semibold text-gray-900">
              {pax.first_name} {pax.last_name}
            </p>
            {pax.nationality && (
              <span className="text-lg">{COUNTRY_FLAGS[pax.nationality] || pax.nationality}</span>
            )}
            {pax.is_main && (
              <Badge variant="secondary" className="text-xs">Principal</Badge>
            )}
            {isAdult && (
              <Badge variant="outline" className="text-xs">{pax.age} ans</Badge>
            )}
          </div>
          
          {/* Info grid */}
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
            <div className="flex items-center gap-1.5 text-gray-600">
              <Calendar className="w-3.5 h-3.5" />
              <span>{safeFormatDate(pax.birth_date)}</span>
            </div>
            <div className="flex items-center gap-1.5 text-gray-600">
              <CreditCard className="w-3.5 h-3.5" />
              <span className="font-mono">{pax.document_number || '-'}</span>
            </div>
            {pax.document_expiry && (
              <div className="flex items-center gap-1.5 text-gray-600 col-span-2">
                <Clock className="w-3.5 h-3.5" />
                <span>Expire: {safeFormatDate(pax.document_expiry)}</span>
              </div>
            )}
          </div>
          
          {/* Status indicators */}
          <div className="flex flex-wrap gap-2 mt-2">
            {pax.is_complete ? (
              <Badge variant="outline" className="text-green-600 border-green-300 text-xs">
                <CheckCircle2 className="w-3 h-3 mr-1" />
                Données OK
              </Badge>
            ) : (
              <Badge variant="outline" className="text-amber-600 border-amber-300 text-xs">
                <AlertTriangle className="w-3 h-3 mr-1" />
                Incomplet
              </Badge>
            )}
            {hasPhoto ? (
              <Badge variant="outline" className="text-green-600 border-green-300 text-xs">
                <Camera className="w-3 h-3 mr-1" />
                Photo OK
              </Badge>
            ) : (
              <Badge variant="outline" className="text-blue-600 border-blue-300 text-xs">
                <Camera className="w-3 h-3 mr-1" />
                Photo requise
              </Badge>
            )}
          </div>
        </div>
        
        {/* Action buttons */}
        <div className="flex flex-col gap-2 shrink-0">
          {!paxLocked && (
            <>
              {/* Edit button */}
              <Button
                size="sm"
                variant="outline"
                onClick={() => onEdit(pax.id)}
                className="border-gray-300"
                title="Modifier"
              >
                <Edit2 className="w-4 h-4" />
              </Button>
              
              {/* Delete button */}
              <Button
                size="sm"
                variant="outline"
                onClick={handleDelete}
                disabled={deleting}
                className="border-red-200 text-red-600 hover:bg-red-50"
                title="Supprimer"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              </Button>
            </>
          )}
          
          {/* Upload photo button */}
          <input
            type="file"
            accept="image/*"
            capture="environment"
            ref={fileInputRef}
            onChange={handleFileChange}
            className="hidden"
          />
          <Button
            size="sm"
            variant={hasPhoto ? "outline" : "default"}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading === pax.id || paxLocked}
            className={hasPhoto ? "border-green-300 text-green-700" : "bg-blue-600 hover:bg-blue-700"}
          >
            {uploading === pax.id ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Camera className="w-4 h-4" />
            )}
          </Button>
        </div>
      </div>
      
      {/* Photo preview */}
      {hasPhoto && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <p className="text-xs text-gray-500 mb-1">Photo passeport</p>
          <img 
            src={`${API_URL}/api/uploads/${pax.passport_photo}`}
            alt="Passeport"
            className="w-full h-24 object-cover rounded-lg"
          />
        </div>
      )}
    </div>
  );
};

export default function CheckInDialog({ 
  open, 
  onOpenChange, 
  reservation,
  onComplete,
  onRefresh 
}) {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [checkinData, setCheckinData] = useState(null);
  const [taxOption, setTaxOption] = useState('now');
  const [editingPax, setEditingPax] = useState(null);
  const [policeFormLoading, setPoliceFormLoading] = useState(false);
  const [showAddPax, setShowAddPax] = useState(false);
  
  useEffect(() => {
    if (open && reservation?.id) {
      fetchCheckinData();
      setEditingPax(null);
      setShowAddPax(false);
    }
  }, [open, reservation?.id]);
  
  const fetchCheckinData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/reservations/${reservation.id}/checkin`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      setCheckinData(data);
    } catch (err) {
      toast.error('Erreur lors du chargement');
    }
  };
  
  const handleUploadPhoto = async (paxId, file) => {
    setUploading(paxId);
    try {
      const formData = new FormData();
      formData.append('document', file);
      
      const response = await fetch(`${API_URL}/api/reservations/${reservation.id}/pax/${paxId}/passport-photo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error('Erreur upload');
      }
      
      toast.success('Photo enregistrée');
      await fetchCheckinData();
    } catch (err) {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploading(null);
    }
  };

  const handlePaxSave = () => {
    setEditingPax(null);
    fetchCheckinData();
    if (onRefresh) onRefresh();
  };

  const handlePaxAdd = () => {
    setShowAddPax(false);
    fetchCheckinData();
    if (onRefresh) onRefresh();
  };

  const handlePaxDelete = () => {
    fetchCheckinData();
    if (onRefresh) onRefresh();
  };

  const handleViewPoliceForm = async () => {
    setPoliceFormLoading(true);
    try {
      // Force regeneration to get latest PAX data
      const response = await fetch(`${API_URL}/api/reservations/${reservation.id}/police-form?regenerate=true`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      
      // Use centralized template
      printPoliceForm(data);
    } catch (err) {
      toast.error('Erreur lors du chargement de la fiche');
    } finally {
      setPoliceFormLoading(false);
    }
  };
  
  const handleCompleteCheckIn = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/reservations/${reservation.id}/checkin/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          tourist_tax_paid: taxOption === 'now',
          defer_tourist_tax: taxOption === 'defer',
          police_form_printed: true
        })
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        if (result.issues) {
          toast.error(
            <div>
              <p className="font-semibold">{result.error}</p>
              <ul className="text-sm mt-1">
                {result.issues.slice(0, 3).map((issue, i) => (
                  <li key={i}>• {issue}</li>
                ))}
              </ul>
            </div>
          );
        } else {
          toast.error(result.error || 'Erreur lors du check-in');
        }
        setLoading(false);
        return;
      }
      
      toast.success('Check-in effectué avec succès !');
      onComplete?.(result);
      onOpenChange(false);
    } catch (err) {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };
  
  if (!reservation) return null;

  if (!checkinData) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }
  
  const paxList = checkinData.pax_list || [];
  const allPaxReady = checkinData.all_pax_ready;
  const canCheckIn = checkinData.can_complete_checkin;
  const paxLocked = checkinData.pax_locked;
  const checklist = checkinData.checklist || {};
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            Check-in - {reservation.guest?.first_name} {reservation.guest?.last_name}
            {paxLocked && (
              <Badge variant="outline" className="ml-2 text-amber-600 border-amber-300">
                <Lock className="w-3 h-3 mr-1" />
                Verrouillé
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>
        
        {/* Add PAX modal */}
        {showAddPax && (
          <AddPaxForm
            reservationId={reservation.id}
            onSave={handlePaxAdd}
            onCancel={() => setShowAddPax(false)}
          />
        )}
        
        <div className="flex-1 overflow-y-auto space-y-6 py-4">
          {/* Reservation summary */}
          <div className="p-4 bg-gray-50 rounded-xl">
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Arrivée</p>
                <p className="font-semibold">{format(new Date(reservation.check_in), 'dd MMM yyyy', { locale: fr })}</p>
              </div>
              <div>
                <p className="text-gray-500">Départ</p>
                <p className="font-semibold">{format(new Date(reservation.check_out), 'dd MMM yyyy', { locale: fr })}</p>
              </div>
              <div>
                <p className="text-gray-500">Nuits</p>
                <p className="font-semibold">{reservation.nights}</p>
              </div>
            </div>
          </div>
          
          {/* Checklist summary */}
          <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
            <h4 className="font-semibold text-blue-900 mb-2">État de la fiche de police</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex items-center gap-2">
                {checklist.has_pax ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <XCircle className="w-4 h-4 text-red-500" />}
                <span>Voyageurs: {paxList.length}/{checkinData.total_pax}</span>
              </div>
              <div className="flex items-center gap-2">
                {checklist.all_pax_complete ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <XCircle className="w-4 h-4 text-red-500" />}
                <span>Données complètes</span>
              </div>
              <div className="flex items-center gap-2">
                {checklist.all_pax_have_photos ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <XCircle className="w-4 h-4 text-red-500" />}
                <span>Photos passeport</span>
              </div>
            </div>
            {allPaxReady && (
              <div className="mt-3 pt-3 border-t border-blue-200 flex items-center justify-between">
                <span className="text-green-700 font-medium flex items-center gap-1">
                  <CheckCircle2 className="w-4 h-4" />
                  Fiche de police prête
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleViewPoliceForm}
                  disabled={policeFormLoading}
                  className="text-blue-700 border-blue-300"
                >
                  {policeFormLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <FileText className="w-4 h-4 mr-1" />}
                  Aperçu
                </Button>
              </div>
            )}
          </div>
          
          {/* PAX verification */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900">Voyageurs</h3>
              <div className="flex items-center gap-2">
                <Badge variant={allPaxReady ? "default" : "secondary"}>
                  {paxList.filter(p => p.is_ready).length} / {paxList.length} prêts
                </Badge>
                {!paxLocked && (
                  <Button size="sm" onClick={() => setShowAddPax(true)} className="bg-green-600 hover:bg-green-700">
                    <Plus className="w-4 h-4 mr-1" />
                    Ajouter
                  </Button>
                )}
              </div>
            </div>
            
            {paxList.length === 0 ? (
              <div className="text-center py-8 text-gray-500 border-2 border-dashed rounded-xl">
                <User className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Aucun voyageur enregistré</p>
                <p className="text-sm mb-4">Ajoutez les voyageurs pour continuer</p>
                {!paxLocked && (
                  <Button onClick={() => setShowAddPax(true)} className="bg-green-600 hover:bg-green-700">
                    <Plus className="w-4 h-4 mr-1" />
                    Ajouter un voyageur
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {paxList.map((pax, index) => (
                  <PaxCard
                    key={pax.id}
                    pax={pax}
                    index={index}
                    onUploadPhoto={handleUploadPhoto}
                    uploading={uploading}
                    onEdit={setEditingPax}
                    onSave={handlePaxSave}
                    editing={editingPax}
                    reservationId={reservation.id}
                    onDelete={handlePaxDelete}
                    paxLocked={paxLocked}
                  />
                ))}
              </div>
            )}
          </div>
          
          {/* Tourist tax section */}
          {checkinData.tourist_tax_applicable && checkinData.tourist_tax > 0 && (
            <div className="border-t pt-4">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Receipt className="w-5 h-5 text-amber-600" />
                Taxe de séjour
              </h3>
              
              <div className="p-4 bg-amber-50 rounded-xl border border-amber-200 mb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-amber-800">
                      {checkinData.tourist_tax_config?.amount_per_unit} {checkinData.tourist_tax_config?.currency} × {reservation.nights} nuits × {checkinData.total_pax} pers.
                    </p>
                    <p className="text-2xl font-bold text-amber-900">
                      {checkinData.tourist_tax} MAD
                    </p>
                  </div>
                  <Banknote className="w-10 h-10 text-amber-600 opacity-50" />
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer hover:bg-gray-50"
                  style={{ borderColor: taxOption === 'now' ? '#16a34a' : '#e5e7eb' }}
                >
                  <input type="radio" name="taxOption" value="now" checked={taxOption === 'now'} onChange={(e) => setTaxOption(e.target.value)} className="w-4 h-4 text-green-600" />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">Encaisser maintenant</p>
                    <p className="text-sm text-gray-500">Le client paie immédiatement</p>
                  </div>
                  <CheckCircle2 className={`w-5 h-5 ${taxOption === 'now' ? 'text-green-600' : 'text-gray-300'}`} />
                </label>
                
                <label className="flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer hover:bg-gray-50"
                  style={{ borderColor: taxOption === 'defer' ? '#2563eb' : '#e5e7eb' }}
                >
                  <input type="radio" name="taxOption" value="defer" checked={taxOption === 'defer'} onChange={(e) => setTaxOption(e.target.value)} className="w-4 h-4 text-blue-600" />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">Différer le paiement</p>
                    <p className="text-sm text-gray-500">Créer un ticket en attente</p>
                  </div>
                  <Clock className={`w-5 h-5 ${taxOption === 'defer' ? 'text-blue-600' : 'text-gray-300'}`} />
                </label>
              </div>
            </div>
          )}
        </div>
        
        <DialogFooter className="border-t pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button 
            onClick={handleCompleteCheckIn}
            disabled={loading || !canCheckIn || paxLocked}
            className={canCheckIn && !paxLocked ? "bg-green-600 hover:bg-green-700" : "bg-gray-400"}
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : paxLocked ? (
              <Lock className="w-4 h-4 mr-2" />
            ) : (
              <CheckCircle2 className="w-4 h-4 mr-2" />
            )}
            {paxLocked ? 'Déjà validé' : 'Valider le check-in'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
